import React, { useState } from "react";
import { Upload, Check, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/i18n/I18nContext";

import { translateApiError } from "@/utils/apiErrors";
import { useToast } from "@/hooks/use-toast";
import api from "@/lib/api";
import { WIZARD } from "@/constants/testIds";

const VOCAL_STYLES = ["standard", "rock", "puissant", "douce", "rappeur", "crooner"];

export default function Step3VoiceVisual({ form, update, pricingConfig }) {
  const { t, language } = useI18n();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/uploads/thumbnail", formData, { headers: { "Content-Type": "multipart/form-data" } });
      update({ thumbnail_upload_path: data.storage_path });
      toast({ title: t("wizard.uploadPhotoSuccess") });
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: translateApiError(err?.response?.data?.detail, language) });
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5">
        <div>
          <Label className="text-base">{t("wizard.voiceLabel")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.voiceEnabledDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.voiceEnabledSwitch} checked={form.voice_enabled} onCheckedChange={(v) => update({ voice_enabled: v })} />
      </div>

      {form.voice_enabled && (
        <>
          <div>
            <Label className="text-base">{t("wizard.voiceGenderLabel")}</Label>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid={WIZARD.voiceGenderHomme}
                onClick={() => update({ voice_gender: "homme" })}
                className={`rounded-xl border p-4 text-center font-medium transition-all ${
                  form.voice_gender === "homme" ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                }`}
              >
                {t("wizard.voiceHomme")}
              </button>
              <button
                type="button"
                data-testid={WIZARD.voiceGenderFemme}
                onClick={() => update({ voice_gender: "femme" })}
                className={`rounded-xl border p-4 text-center font-medium transition-all ${
                  form.voice_gender === "femme" ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                }`}
              >
                {t("wizard.voiceFemme")}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-base">{t("wizard.vocalStyleLabel")}</Label>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {VOCAL_STYLES.map((vs) => (
                <button
                  key={vs}
                  type="button"
                  data-testid={WIZARD.vocalStyleOption(vs)}
                  onClick={() => update({ vocal_style: vs })}
                  className={`rounded-xl border px-3 py-3 text-sm font-medium transition-all ${
                    form.vocal_style === vs ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {t(`wizard.vocal${vs.charAt(0).toUpperCase()}${vs.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="rounded-2xl border border-border bg-card/50 p-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">{t("wizard.thumbnailLabel")}</Label>
            <p className="mt-1 text-sm text-muted-foreground">{t("wizard.thumbnailDesc")}</p>
          </div>
          <Switch data-testid={WIZARD.thumbnailSwitch} checked={form.thumbnail_ai} onCheckedChange={(v) => update({ thumbnail_ai: v })} />
        </div>

        {form.thumbnail_ai && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid={WIZARD.thumbnailSourceAi}
              onClick={() => update({ thumbnail_source: "ai" })}
              className={`rounded-xl border p-3 text-sm font-medium transition-all ${
                form.thumbnail_source === "ai" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t("wizard.thumbnailSourceAi")}
            </button>
            <button
              type="button"
              data-testid={WIZARD.thumbnailSourceUpload}
              onClick={() => update({ thumbnail_source: "upload" })}
              className={`rounded-xl border p-3 text-sm font-medium transition-all ${
                form.thumbnail_source === "upload" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t("wizard.thumbnailSourceUpload")}
            </button>
          </div>
        )}

        {form.thumbnail_ai && form.thumbnail_source === "upload" && (
          <div className="mt-3">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground hover:border-primary/40">
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : form.thumbnail_upload_path ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? t("wizard.uploadPhotoUploading") : form.thumbnail_upload_path ? t("wizard.uploadPhotoSuccess") : t("wizard.uploadPhotoCta")}
              <Input type="file" accept="image/jpeg,image/png,image/webp" data-testid={WIZARD.uploadPhotoInput} className="hidden" onChange={handleFileChange} />
            </label>
          </div>
        )}
      </div>

      <div>
        <Label className="text-base">{t("wizard.titleModeLabel")}</Label>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            data-testid={WIZARD.titleModeAuto}
            onClick={() => update({ title_mode: "auto" })}
            className={`rounded-xl border p-4 text-left transition-all ${
              form.title_mode === "auto" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
            }`}
          >
            <p className="font-medium">{t("wizard.titleAuto")}</p>
          </button>
          <button
            type="button"
            data-testid={WIZARD.titleModeCustom}
            onClick={() => update({ title_mode: "custom" })}
            className={`rounded-xl border p-4 text-left transition-all ${
              form.title_mode === "custom" ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
            }`}
          >
            <p className="font-medium">{t("wizard.titleCustom")}</p>
          </button>
        </div>
        {form.title_mode === "custom" && (
          <Input
            data-testid={WIZARD.customTitleInput}
            placeholder={t("wizard.customTitlePlaceholder")}
            value={form.custom_title}
            onChange={(e) => update({ custom_title: e.target.value })}
            className="mt-3 rounded-xl"
          />
        )}
      </div>
    </div>
  );
}
